// Custom DS-Table column that renders its cell as a real hyperlink — a design-system Link, i.e. a genuine
// <a href> — instead of the DS TextColumn's clickable <span onClick>. Because it is a real anchor, the
// browser offers "Open in new tab", ctrl/cmd-click, middle-click and right-click (which a span onClick does
// not). A plain left-click still SPA-navigates via onOpen; a modified click falls through to the browser so
// it opens a new tab/window. Used for the Opportunity name (with its coloured id badge) and the Client cell.
// (Dist-internal imports are the accepted web-front convention for custom DS columns — see ContactColumn.)
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Link} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import Styled from 'styled-components';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import {isModifiedClick} from '../../shared/util/LinkClickUtil';

export interface LinkColumnType {
	id?: string;
	accessorKey: string; // supplies the displayed text (via getValue) and the sort value
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// Optional leading element (e.g. the coloured id badge); omitted for a plain link cell.
	icon?: (row: OpportunityTableRow) => React.ReactNode;
	// The internal route for the anchor's href. undefined → plain text (no link), e.g. a row with no client.
	href: (row: OpportunityTableRow) => string | undefined;
	// SPA navigation for a plain left-click.
	onOpen: (row: OpportunityTableRow) => void;
}

const Layout = Styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	max-width: 100%;
`;
const Ellipsis = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LinkColumn = (_: LinkColumnType): null => null;

LinkColumn.toColumnDef = (child: React.ReactElement<LinkColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row, getValue}: {row: {original: OpportunityTableRow}; getValue: () => unknown}) => {
		const rowData = row.original;
		const text = (getValue() as string) ?? '';
		const href = child.props.href(rowData);
		const icon = child.props.icon ? child.props.icon(rowData) : null;
		return (
			<StyledCell align={child.props.align}>
				<Layout>
					{icon}
					{/* title surfaces the full value on hover — the cell ellipsis-truncates long names. */}
					<Ellipsis title={text || undefined}>
						{href ? (
							<Link
								href={href}
								data-cy={child.props['data-cy']}
								onClick={(e: React.MouseEvent) => {
									if (isModifiedClick(e)) return; // let the browser open a new tab / window
									e.preventDefault();
									child.props.onOpen(rowData);
								}}
							>
								{text}
							</Link>
						) : (
							text
						)}
					</Ellipsis>
				</Layout>
			</StyledCell>
		);
	},
});

export default LinkColumn;
